import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import api from '../../../lib/api'
import { formatDate } from '../../../lib/formatters'
import type { WorkOrder, PaginatedResponse, WorkOrderStatus } from '../../../types'

const STATUS_OPTIONS: { value: WorkOrderStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'received', label: 'Received' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
]

export default function WorkOrdersListPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [priority, setPriority] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', page, search, status, priority],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '10' })
      if (search) params.append('search', search)
      if (status) params.append('status', status)
      if (priority) params.append('priority', priority)
      const { data } = await api.get<PaginatedResponse<WorkOrder>>(`/work-orders?${params}`)
      return data
    },
  })

  const getStatusBadge = (orderStatus: WorkOrderStatus) => {
    const variants: Record<WorkOrderStatus, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
      draft: 'gray',
      received: 'info',
      in_progress: 'warning',
      completed: 'success',
      delivered: 'success',
      cancelled: 'error',
    }
    return <Badge variant={variants[orderStatus] || 'gray'}>{orderStatus ? String(orderStatus).replace('_', ' ') : 'unknown'}</Badge>
  }

  const getPriorityBadge = (orderPriority: string) => {
    const variants: Record<string, 'error' | 'warning' | 'info' | 'gray'> = {
      urgent: 'error',
      high: 'warning',
      normal: 'info',
      low: 'gray',
    }
    return <Badge variant={variants[orderPriority] || 'gray'}>{orderPriority}</Badge>
  }

  const columns = [
    {
      key: 'displayId',
      header: 'Order ID',
      render: (order: WorkOrder) => (
        <Link to={`/work-orders/${order.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
          {order.displayId}
        </Link>
      ),
    },
    {
      key: 'doctor',
      header: 'Doctor / Patient',
      render: (order: WorkOrder) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{order.doctor?.name ?? '-'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {order.patientName ?? '-'}
          </p>
        </div>
      ),
    },
    {
      key: 'items',
      header: 'Items',
      render: (order: WorkOrder) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {order.items?.length ?? 0} item(s)
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (order: WorkOrder) => getPriorityBadge(order.priority),
    },
    {
      key: 'status',
      header: 'Status',
      render: (order: WorkOrder) => getStatusBadge(order.status),
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      render: (order: WorkOrder) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {order.assignedUser?.name || <span className="text-gray-400 italic">Unassigned</span>}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      render: (order: WorkOrder) => {
        const isOverdue = new Date(order.dueDate) < new Date() &&
          !['completed', 'delivered', 'cancelled'].includes(order.status)
        return (
          <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
            {formatDate(order.dueDate)}
            {isOverdue && <i className="fas fa-exclamation-circle ml-1"></i>}
          </span>
        )
      },
    },
    {
      key: 'actions',
      header: '',
      render: (order: WorkOrder) => (
        <div className="flex justify-end gap-2">
          <Link
            to={`/work-orders/${order.id}`}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            <i className="fas fa-eye"></i>
          </Link>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Work Orders</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track and manage all work orders
          </p>
        </div>
        <Link to="/work-orders/new">
          <Button>
            <i className="fas fa-plus mr-2"></i>
            New Order
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search by ID, doctor, patient..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { 
                window.location.href = `/work-orders/${option.id}`
              }}
              queryKey={['work-orders', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<PaginatedResponse<WorkOrder>>('/work-orders?limit=100')
                  return (data.data || []).map((order: WorkOrder): TypeaheadOption => ({
                    id: order.id,
                    label: order.displayId,
                    sublabel: `${order.doctor?.name || 'Unknown'} • ${(order.status || '').replace('_', ' ')}`,
                    icon: 'fa-clipboard-list',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
          <SearchableSelect
            value={status}
            onChange={(val) => { setStatus(val); setPage(1) }}
            placeholder="All Status"
            searchable={false}
            options={STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
          />
          <SearchableSelect
            value={priority}
            onChange={(val) => { setPriority(val); setPage(1) }}
            placeholder="All Priority"
            searchable={false}
            options={PRIORITY_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
          />
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Pending', count: data?.data?.filter(o => ['draft', 'received'].includes(o.status)).length ?? 0, color: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30' },
          { label: 'In Progress', count: data?.data?.filter(o => o.status === 'in_progress').length ?? 0, color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30' },
          { label: 'Completed', count: data?.data?.filter(o => o.status === 'completed').length ?? 0, color: 'text-green-600 bg-green-100 dark:bg-green-900/30' },
          { label: 'Delivered', count: data?.data?.filter(o => o.status === 'delivered').length ?? 0, color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30' },
        ].map((stat) => (
          <Card key={stat.label} padding="sm">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                <span className="font-bold">{stat.count}</span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(order) => order.id}
          emptyMessage="No work orders found"
          onRowClick={(order) => window.location.href = `/work-orders/${order.id}`}
        />
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  )
}
